/**
 * The payee page's Aliases tab (TMMAPP-0123 §D; TMMAPP-0124 item 1): every alias with its type —
 * "Exact", "Contains" or "Pattern". A pattern alias shows its text in mono (an identifier, TMM-T1)
 * and its match count. The payee's place in the matching order lives in the page header, not on
 * each alias. Each row has Edit (the alias dialog) and Remove, which asks first: the server's dry
 * run, then keep the names or re-match the lines (`AliasRemoveDialog`, TMMAPP-0142 §D). Add alias
 * opens the same dialog; for a non-subscriber it opens the purchase experience
 * (`useSubscriberAction`).
 */
import { useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { errorCopy } from '@/api/errorCopy';
import { payeesApi, type AliasRemovalLines, type Payee, type PayeeAlias } from '@/api/payees';
import { TabularText } from '@/components/TabularText';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import { AliasDialog } from './AliasDialog';
import { AliasRemoveDialog, type AliasRemovalCheck } from './AliasRemoveDialog';
import { ALIAS_KIND_KEYS, kindOf } from './aliasKind';

interface AliasesTabProps {
  payee: Payee;
  readOnly: boolean;
  /** Re-reads the payee (quietly) after a write. */
  onChanged: () => Promise<void>;
  onNotify: (message: string) => void;
}

export function AliasesTab({ payee, readOnly, onChanged, onNotify }: AliasesTabProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<{ alias: PayeeAlias | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<{
    alias: PayeeAlias;
    check: AliasRemovalCheck;
  } | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  // A dry run that answers after its dialog closed (or another opened) is dropped.
  const checkTicket = useRef(0);
  const add = useSubscriberAction(() => setEditing({ alias: null }));
  // The removed row's index: once the write and the re-read settle (the buttons are enabled again),
  // focus moves to the Remove of the row now in its place (or the new last row), or to Add alias
  // when the list has emptied, never <body> (TMMAPP-0144 item 3, as TMMAPP-0143 does).
  const listRef = useRef<HTMLDivElement | null>(null);
  const addRef = useRef<HTMLButtonElement | null>(null);
  const focusIndex = useRef<number | null>(null);
  useEffect(() => {
    if (busy || focusIndex.current === null) return;
    const index = focusIndex.current;
    focusIndex.current = null;
    const removes = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>('[data-alias-remove]') ?? [],
    );
    (removes[Math.min(index, removes.length - 1)] ?? addRef.current)?.focus();
  }, [busy, payee]);

  async function askRemove(alias: PayeeAlias) {
    checkTicket.current += 1;
    const ticket = checkTicket.current;
    setRemoveError(null);
    setRemoving({ alias, check: { status: 'loading' } });
    let check: AliasRemovalCheck;
    try {
      check = { status: 'ready', preview: await payeesApi.previewAliasRemoval(payee.id, alias.id) };
    } catch (err) {
      check = { status: 'failed', message: errorCopy(t, err, 'aliasRemove.checkFailed') };
    }
    if (ticket === checkTicket.current) setRemoving({ alias, check });
  }

  function closeRemove() {
    checkTicket.current += 1;
    setRemoving(null);
    setRemoveError(null);
  }

  async function remove(alias: PayeeAlias, lines: AliasRemovalLines) {
    if (busy) return;
    setBusy(true);
    setRemoveError(null);
    try {
      await payeesApi.removeAlias(payee.id, alias.id, lines);
      const index = payee.aliases.findIndex((other) => other.id === alias.id);
      await onChanged();
      focusIndex.current = index;
      closeRemove();
    } catch (err) {
      setRemoveError(errorCopy(t, err, 'payee.removeAliasFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack ref={listRef} spacing={1.5}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('payee.aliasHelp', { payee: payee.name })}
      </Typography>
      {payee.aliases.map((alias) => {
        const kind = kindOf(alias.matchType);
        const text = alias.pattern ?? alias.payeeNorm;
        return (
          <Box
            key={alias.id}
            data-alias={text}
            data-alias-kind={kind}
            sx={{ borderBottom: 1, borderColor: 'divider', pb: 1.5 }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              sx={{ gap: 1, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
            >
              <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip size="small" label={t(ALIAS_KIND_KEYS[kind])} />
                  <Typography variant="mono" sx={{ overflowWrap: 'anywhere' }}>
                    {text}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <TabularText variant="body2">
                    {t('payee.aliasMatches', {
                      count: alias.hits,
                    })}
                  </TabularText>
                </Typography>
              </Stack>
              {!readOnly && (
                <Stack direction="row" sx={{ gap: 1, flexShrink: 0 }}>
                  <Button
                    size="small"
                    disabled={busy}
                    aria-label={t('payee.editAliasFor', { alias: text })}
                    onClick={() => setEditing({ alias })}
                  >
                    {t('common.edit')}
                  </Button>
                  <Button
                    size="small"
                    disabled={busy}
                    aria-haspopup="dialog"
                    data-alias-remove
                    onClick={() => void askRemove(alias)}
                  >
                    {t('payee.removeAlias')}
                  </Button>
                </Stack>
              )}
            </Stack>
          </Box>
        );
      })}
      <Box>
        <Button
          ref={addRef}
          variant="outlined"
          aria-describedby={add.descriptionId}
          onClick={add.run}
          disabled={busy}
        >
          {t('payee.addAlias')}
        </Button>
        <SubscriberActionHint id={add.descriptionId} description={add.description} />
      </Box>
      {removing !== null && (
        <AliasRemoveDialog
          alias={removing.alias}
          check={removing.check}
          busy={busy}
          error={removeError}
          onConfirm={(lines) => void remove(removing.alias, lines)}
          onCancel={closeRemove}
        />
      )}
      {editing !== null && (
        <AliasDialog
          payee={payee}
          alias={editing.alias}
          onClose={() => setEditing(null)}
          onSaved={onChanged}
          onNotify={onNotify}
        />
      )}
    </Stack>
  );
}
